import { useMutation } from '@tanstack/react-query';
import { usePostHog } from 'posthog-js/react';
import { useAppDispatch, useAppSelector } from '../useAppStore';
import { getAuthenticateUser } from '../../store/authenticate/selectors';
import { IBaseResponse, TAxiosError, TGetSettingsRes } from '../../types/responses';
import { settingsActions } from '../../store/settings/slice';
import { SettingsController } from '../../services/settings.api';
import { TSettingPermissions } from '../../types/settings.type';
import { useError } from '../useCommon';
import { TUpdateSettingsReq } from '../../types/requests';

export function useGetSettingsAPI() {
  const dispatch = useAppDispatch();
  const { handleReqError } = useError();

  const getSettingsApi = (shopName: string) => {
    const data = {
      shopName,
    };

    return SettingsController().getSettings<TGetSettingsRes>(data);
  };

  return useMutation(getSettingsApi, {
    onSuccess: (rsp, variables) => {
      if (rsp?.success && 'settings' in rsp) {
        dispatch(settingsActions.setManagerPermissions(rsp.settings.managerPermissions));
        dispatch(settingsActions.setEmployeePermissions(rsp.settings.employeePermissions));
        dispatch(settingsActions.setIntegrations(rsp.settings.integrations));
      } else if (rsp?.error && rsp.error?.response?.status) {
        handleReqError(rsp.error, variables);
      } else if ('msg' in rsp) {
        console.error(`ERROR! get settings failed! ${rsp.msg}`);
      }
    },
    onError: (error: TAxiosError) => {
      console.error(`ERROR! get settings request threw an Exception! ${error}`);
    },
  });
}

export function useUpdateManagerPermissions() {
  const dispatch = useAppDispatch();
  const { personnel } = useAppSelector(getAuthenticateUser);
  const { handleReqError } = useError();
  const posthog = usePostHog();

  const updateManagerPermissionsApi = (payload: TSettingPermissions) => {
    const data = {
      shopName: personnel.shopName,
      permissions: payload,
    };

    return SettingsController().updateManagerPermissions<IBaseResponse>(data);
  };

  return useMutation(updateManagerPermissionsApi, {
    onSuccess: (rsp, variables) => {
      posthog.capture('ManageMate_web', {
        shopName: personnel?.shopName,
        email: personnel?.email,
        addedAt: new Date().toISOString(),
        action: 'manager_permissions_changed',
      });

      if (rsp?.success) {
        dispatch(settingsActions.setManagerPermissions(variables));
      } else if (rsp?.error && rsp.error?.response?.status) {
        handleReqError(rsp.error, variables);
      } else if ('msg' in rsp) {
        console.error(`ERROR! update manager permissions failed! ${rsp.msg}`);
      }
    },
    onError: (error: TAxiosError) => {
      console.error(`ERROR! update manager permissions request threw an Exception! ${error}`);
    },
  });
}

export function useUpdateEmployeePermissions() {
  const dispatch = useAppDispatch();
  const { personnel } = useAppSelector(getAuthenticateUser);
  const { handleReqError } = useError();
  const posthog = usePostHog();

  const updateEmployeePermissionsApi = (payload: TSettingPermissions) => {
    const data = {
      shopName: personnel.shopName,
      permissions: payload,
    };

    return SettingsController().updateEmployeePermissions<IBaseResponse>(data);
  };

  return useMutation(updateEmployeePermissionsApi, {
    onSuccess: (rsp, variables) => {
      posthog.capture('ManageMate_web', {
        shopName: personnel?.shopName,
        email: personnel?.email,
        addedAt: new Date().toISOString(),
        action: 'employee_permissions_changed',
      });

      if (rsp?.success) {
        dispatch(settingsActions.setEmployeePermissions(variables));
      } else if (rsp?.error && rsp.error?.response?.status) {
        handleReqError(rsp.error, variables);
      } else if ('msg' in rsp) {
        console.error(`ERROR! update employee permissions failed! ${rsp.msg}`);
      }
    },
    onError: (error: TAxiosError) => {
      console.error(`ERROR! update employee permissions request threw an Exception! ${error}`);
    },
  });
}

export function useUpdateSettingsAPI() {
  const dispatch = useAppDispatch();
  const { personnel } = useAppSelector(getAuthenticateUser);
  const { handleReqError } = useError();

  const updateSettingsApi = (payload: {
    employee: TSettingPermissions,
    manager: TSettingPermissions,
  }) => {
    const data = {
      shopName: personnel.shopName,
      settings: {
        employeePermissions: payload.employee,
        managerPermissions: payload.manager,
      },
    };

    return SettingsController().updateSettings<IBaseResponse>(data as TUpdateSettingsReq);
  };

  return useMutation(updateSettingsApi, {
    onSuccess: (rsp, variables) => {
      if (rsp?.success) {
        dispatch(settingsActions.setManagerPermissions(variables.manager));
        dispatch(settingsActions.setEmployeePermissions(variables.employee));
      } else if (rsp?.error && rsp.error?.response?.status) {
        handleReqError(rsp.error, variables);
      } else if ('msg' in rsp) {
        console.error(`ERROR! update settings failed! ${rsp.msg}`);
      }
    },
    onError: (error: TAxiosError) => {
      console.error(`ERROR! update settings request threw an Exception! ${error}`);
    },
  });
}
